import { byId, type Ledger } from './rows.js';

/**
 * What the bank said about one transaction (ADR-0025).
 *
 * The rows this reads — `transactionBankRef`, `bankAccount`,
 * `bankConnection` — were originally kept off the device on two grounds, and
 * only one of them survived contact with the data. The size argument was
 * simply wrong: measured against the real ledger, a `bankRaw` payload averages
 * ~200 bytes and the whole set is 15 KB. The other, that a device holding a
 * copy of somebody's bank feed is exposure for little gain, is a judgement,
 * and the users made it the other way — so the reference rows travel and the
 * consent internals (a session id, a provider's raw account payload) do not.
 * See the `replicate: false` fields in the manifest.
 */

export interface BankInfoSource {
  bank: string;
  country: string;
  account: string;
  iban: string;
  currency: string;
  reference: string;
  feed: string;
}

export interface BankInfoEntry {
  source: BankInfoSource;
  raw: unknown;
}

/** `bankRaw` is text in the row; a malformed one is null rather than a throw. */
function parseRaw(raw: unknown): unknown {
  if (typeof raw !== 'string' || raw === '') return null;
  try {
    const value: unknown = JSON.parse(raw);
    return typeof value === 'object' && value !== null ? value : null;
  } catch {
    return null;
  }
}

/**
 * Both lookups are optional on purpose.
 *
 * Disconnecting a bank clears `bankAccountId` while keeping the feed key —
 * that is exactly what stops a reconnect re-importing everything — so a
 * reference routinely outlives the account and connection it came through.
 * Those entries show what is still true and leave the bank's name empty
 * rather than disappearing.
 */
export function bankInfoOf(ledger: Ledger, transactionId: string): BankInfoEntry[] {
  const accounts = byId(ledger.bankAccounts());
  const connections = byId(ledger.bankConnections());
  return ledger.transactionBankRefs()
    .filter((r) => r.transactionId === transactionId)
    .map((ref) => {
      const account = ref.bankAccountId ? accounts.get(ref.bankAccountId) : undefined;
      const connection = account?.connectionId ? connections.get(account.connectionId) : undefined;
      return {
        source: {
          bank: connection?.aspspName ?? '',
          country: connection?.aspspCountry ?? '',
          account: account?.name ?? '',
          iban: account?.iban ?? '',
          currency: account?.currency ?? '',
          reference: ref.externalRef,
          feed: ref.externalSrc,
        },
        raw: parseRaw(ref.bankRaw),
      };
    });
}
